import * as React from 'react';

import { cn } from '@sara/ui/lib/utils';

/**
 * DataPanel — shared editorial chrome for list/table surfaces.
 *
 * Outer wrapper provides the rounded card surround and clip; consumers compose
 * their own row markup (a <ul> with divide-y, or a <Table>) inside DataPanel.Body.
 *
 * Compound API:
 *   <DataPanel>
 *     <DataPanel.Header columns="1fr auto">…</DataPanel.Header>
 *     <DataPanel.Body>…</DataPanel.Body>
 *     <DataPanel.Footer>…</DataPanel.Footer>
 *   </DataPanel>
 */

type DivProps = React.HTMLAttributes<HTMLDivElement>;

const DataPanelRoot = React.forwardRef<HTMLDivElement, DivProps>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn('overflow-hidden rounded-xl border border-border bg-card', className)}
    {...props}
  />
));
DataPanelRoot.displayName = 'DataPanel';

interface DataPanelHeaderProps extends DivProps {
  /**
   * CSS grid-template-columns value applied via inline style. Use this to
   * declare the row layout (e.g. `"1.4fr 1fr 1fr auto"`). Consumers may still
   * pass `grid-cols-[…]` via `className` for backwards compatibility, but
   * `columns` is the preferred path because it makes the intent explicit and
   * avoids the silent single-column collapse when the template is forgotten.
   */
  columns?: string;
}

const DataPanelHeader = React.forwardRef<HTMLDivElement, DataPanelHeaderProps>(
  ({ className, columns, style, ...props }, ref) => (
    <div
      ref={ref}
      className={cn(
        'grid items-center gap-6 border-b border-border bg-secondary/40 px-6 py-3 text-[11px] font-medium uppercase tracking-[0.06em] text-muted-foreground',
        className
      )}
      style={columns ? { gridTemplateColumns: columns, ...style } : style}
      {...props}
    />
  )
);
DataPanelHeader.displayName = 'DataPanel.Header';

const DataPanelBody = React.forwardRef<HTMLDivElement, DivProps>(({ className, ...props }, ref) => (
  <div ref={ref} className={cn('overflow-x-auto', className)} {...props} />
));
DataPanelBody.displayName = 'DataPanel.Body';

const DataPanelFooter = React.forwardRef<HTMLDivElement, DivProps>(
  ({ className, ...props }, ref) => (
    <div
      ref={ref}
      className={cn(
        'flex items-center justify-between gap-6 border-t border-border bg-secondary/30 px-6 py-3 text-[12px] text-muted-foreground',
        className
      )}
      {...props}
    />
  )
);
DataPanelFooter.displayName = 'DataPanel.Footer';

type DataPanelComponent = typeof DataPanelRoot & {
  Header: typeof DataPanelHeader;
  Body: typeof DataPanelBody;
  Footer: typeof DataPanelFooter;
};

const DataPanel = DataPanelRoot as DataPanelComponent;
DataPanel.Header = DataPanelHeader;
DataPanel.Body = DataPanelBody;
DataPanel.Footer = DataPanelFooter;

export { DataPanel };
